import type {BridgeUser, NavLink, SolutionLink, UserLink} from "../../backend";
import {modeToTarget} from "../../backend";
import {resolveInterstateIcon} from "../../interstate";

export type SideNavIconSize = "md" | "lg";

export type SideNavIcon =
  | {svg: string; size: SideNavIconSize}
  | {logoUrl: string};

export const SIDE_NAV_MENU: UserLink["menu"] = "side nav";

export interface SideNavEntry {
  testId: string;
  href: string | undefined;
  /** Sanitized, then rendered as HTML. */
  label: string;
  fullLabel?: string;
  icon?: SideNavIcon | null;
  target?: string;
}

export function hasUsableUri(link: {uri?: string | null}): boolean {
  return typeof link.uri === "string" && link.uri.trim().length > 0;
}

export function testIdSlug(link: Pick<NavLink, "uri" | "label">): string {
  const key = link.uri?.trim() || link.label?.trim() || "";
  return key
    .replace(/^[a-z]+:\/\//i, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function toNavLinkEntry(
  link: NavLink,
  testIdPrefix: string,
  icon: SideNavIcon | null
): SideNavEntry {
  return {
    testId: `${testIdPrefix}-${testIdSlug(link)}`,
    href: link.uri,
    label: link.label,
    icon,
    target: modeToTarget(link.mode)
  };
}

export function fromUserLink(link: UserLink): SideNavEntry {
  const svg = resolveInterstateIcon(link.icon);
  return toNavLinkEntry(link, "side-nav-item", svg ? {svg, size: "md"} : null);
}

export function fromSolutionLink(link: SolutionLink): SideNavEntry {
  const entry = toNavLinkEntry(
    link,
    "side-nav-solution-item",
    link.logoUrl ? {logoUrl: link.logoUrl} : null
  );
  const label = link.shortName?.trim() || link.label;
  return {
    ...entry,
    label,
    fullLabel: label !== link.label ? link.label : undefined
  };
}

export function sideNavUserLinks(user: BridgeUser): UserLink[] {
  return (user.user_links ?? []).filter(
    link => link.menu === SIDE_NAV_MENU && hasUsableUri(link)
  );
}
